import React from 'react';
import { ArrowLeft, Globe, Users, Star, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
    const navigate = useNavigate();

    return (
        <div className="p-4 max-w-2xl mx-auto pb-24">
            <header className="flex items-center mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="mr-3 p-2 rounded-full hover:bg-gray-100 text-gray-600"
                >
                    <ArrowLeft size={24} />
                </button>
                <h1 className="text-xl font-bold text-gray-900">About Us</h1>
            </header>

            <div className="space-y-8">
                <section>
                    <h2 className="text-2xl font-bold text-blue-900 mb-2">About LocalAura</h2>
                    <p className="text-lg font-medium text-gray-700">Your Universal Community and Service Hub</p>
                </section>

                <section className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                    <h3 className="text-lg font-bold text-blue-800 mb-3">Our Mission</h3>
                    <p className="text-gray-700 leading-relaxed">
                        At LocalAura, we believe that connecting with your local community—for services, events, and experiences—should be simple, efficient, and inspiring, no matter where you are in the world.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                        Our mission is to be the universal hub that seamlessly connects service seekers with skilled service providers and locals with exciting, relevant, local events.
                    </p>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">What is LocalAura?</h3>
                    <p className="text-gray-600 mb-6">
                        Localaura is your comprehensive digital marketplace for all things local. We bridge the gap between people who need help and people who offer skills, while also making sure you never miss out on the best of what your neighborhood has to offer.
                    </p>

                    <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="flex items-center mb-2">
                                <Users className="text-blue-600 mr-2" size={20} />
                                <h4 className="font-bold text-gray-900">Local Services</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-semibold text-gray-700 block mb-1">For Seekers:</span>
                                    <p className="text-gray-600">Find trusted, verified professionals (plumbers, tutors, artists) with transparent reviews.</p>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-700 block mb-1">For Providers:</span>
                                    <p className="text-gray-600">Access highly qualified leads and build your professional reputation with public ratings.</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="flex items-center mb-2">
                                <Star className="text-yellow-500 mr-2" size={20} />
                                <h4 className="font-bold text-gray-900">Community Events</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-semibold text-gray-700 block mb-1">For Event Goers:</span>
                                    <p className="text-gray-600">Discover a curated feed of local happenings based on your personal interests.</p>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-700 block mb-1">For Organizers:</span>
                                    <p className="text-gray-600">Promote your event directly to a targeted local audience actively seeking things to do.</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="flex items-center mb-2">
                                <Globe className="text-green-600 mr-2" size={20} />
                                <h4 className="font-bold text-gray-900">Global Reach</h4>
                            </div>
                            <p className="text-sm text-gray-600">
                                Find any service or event, in any city, making travel planning and relocating easier. Expand your brand and service area beyond traditional local advertising.
                            </p>
                        </div>
                    </div>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Our Vision</h3>
                    <p className="text-gray-600 mb-4">
                        While we operate on a global scale, our focus remains deeply local. We understand that a great service provider is often just around the corner, and the best local experiences are often hidden.
                    </p>
                    <ul className="space-y-3">
                        <li className="flex items-start">
                            <CheckCircle className="text-blue-500 mr-3 mt-1 flex-shrink-0" size={18} />
                            <div>
                                <span className="font-bold text-gray-800">Relevance:</span>
                                <p className="text-sm text-gray-600">Delivering search results that match your needs perfectly.</p>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <CheckCircle className="text-blue-500 mr-3 mt-1 flex-shrink-0" size={18} />
                            <div>
                                <span className="font-bold text-gray-800">Transparency:</span>
                                <p className="text-sm text-gray-600">Ensuring honest user reviews and clear event details.</p>
                            </div>
                        </li>
                        <li className="flex items-start">
                            <CheckCircle className="text-blue-500 mr-3 mt-1 flex-shrink-0" size={18} />
                            <div>
                                <span className="font-bold text-gray-800">Simplicity:</span>
                                <p className="text-sm text-gray-600">Providing a user-friendly platform that makes connecting effortless.</p>
                            </div>
                        </li>
                    </ul>
                </section>

                <section className="bg-gray-900 text-white p-6 rounded-xl">
                    <h3 className="text-lg font-bold mb-3">The LocalAura Difference</h3>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                        Localaura is the single, trusted bridge that turns a need (a service) into a satisfied experience, and turns curiosity (an interest) into a memorable community moment.
                    </p>
                    <p className="font-medium text-blue-200">
                        Ready to find your local professional, or discover the best event happening this weekend?
                    </p>
                </section>
            </div>
        </div>
    );
};

export default AboutUs;
